import { useCallback, useEffect, useRef, useState } from "react";
import { Call, Device } from "@twilio/voice-sdk";
import { api } from "~api";
import {
  SellerAdminChatContact,
  SellerAdminChatVoiceStatus,
} from "../types";
import {
  VoiceCallDisplay,
  VoiceCallTarget,
  buildVoiceConnectParams,
  resolveIncomingCallDisplay,
} from "../utils/voiceCall";

type VoiceTokenResponse = {
  success: boolean;
  data: SellerAdminChatVoiceStatus & {
    token: string | null;
    identity: string;
    name?: string;
    photo_url?: string | null;
  };
};

function bindCallLifecycle(
  call: Call,
  onConnected: () => void,
  onEnded: () => void,
) {
  call.on("accept", onConnected);
  call.on("disconnect", onEnded);
  call.on("cancel", onEnded);
  call.on("reject", onEnded);
}

export function useSellerAdminVoice(
  voice?: SellerAdminChatVoiceStatus,
  contacts: SellerAdminChatContact[] = [],
) {
  const deviceRef = useRef<Device | null>(null);
  const activeCallRef = useRef<Call | null>(null);
  const callerProfileRef = useRef<VoiceCallDisplay>({
    identity: "",
    name: "Administrador",
    photoUrl: null,
  });
  const contactsRef = useRef(contacts);

  const [status, setStatus] = useState<"idle" | "connecting" | "in-call">(
    "idle",
  );
  const [incomingCall, setIncomingCall] = useState<VoiceCallDisplay | null>(
    null,
  );
  const [activeCallDisplay, setActiveCallDisplay] =
    useState<VoiceCallDisplay | null>(null);
  const [callStartedAt, setCallStartedAt] = useState<number | null>(null);
  const [muted, setMuted] = useState(false);

  contactsRef.current = contacts;

  const resetCallState = useCallback(() => {
    activeCallRef.current = null;
    setStatus("idle");
    setIncomingCall(null);
    setActiveCallDisplay(null);
    setCallStartedAt(null);
    setMuted(false);
  }, []);

  const fetchToken = useCallback(async () => {
    const { data } = await api.get<VoiceTokenResponse>(
      "/admin/seller-chat/voice/token",
    );

    if (!data.success || !data.data.token) {
      throw new Error("Token de voz indisponível");
    }

    callerProfileRef.current = {
      identity: data.data.identity,
      name: data.data.name || "Administrador",
      photoUrl: data.data.photo_url ?? null,
    };

    return data.data;
  }, []);

  const ensureDevice = useCallback(async () => {
    if (!voice?.enabled) {
      throw new Error("Ligação por voz não configurada");
    }

    if (deviceRef.current?.state === Device.State.Registered) {
      return deviceRef.current;
    }

    deviceRef.current?.destroy();
    deviceRef.current = null;

    const tokenData = await fetchToken();
    const device = new Device(tokenData.token!, {
      logLevel: 1,
      codecPreferences: ["opus", "pcmu"],
    });

    device.on("tokenWillExpire", async () => {
      try {
        const refreshed = await fetchToken();
        device.updateToken(refreshed.token!);
      } catch (error) {
        console.error("Erro ao renovar token de voz:", error);
      }
    });

    device.on("incoming", (call) => {
      activeCallRef.current = call;
      setIncomingCall(
        resolveIncomingCallDisplay(call, contactsRef.current),
      );
    });

    await device.register();
    deviceRef.current = device;

    return device;
  }, [fetchToken, voice?.enabled]);

  const hangUp = useCallback(() => {
    activeCallRef.current?.disconnect();
    resetCallState();
  }, [resetCallState]);

  const toggleMute = useCallback(() => {
    const call = activeCallRef.current;
    if (!call) return;

    const nextMuted = !muted;
    call.mute(nextMuted);
    setMuted(nextMuted);
  }, [muted]);

  const call = useCallback(
    async (target: VoiceCallTarget) => {
      if (activeCallRef.current) {
        hangUp();
        return;
      }

      const display: VoiceCallDisplay = {
        identity: target.identity,
        name: target.name,
        photoUrl: target.photoUrl ?? null,
      };

      setActiveCallDisplay(display);
      setStatus("connecting");

      try {
        const device = await ensureDevice();
        const nextCall = await device.connect({
          params: buildVoiceConnectParams(target, callerProfileRef.current),
        });
        activeCallRef.current = nextCall;

        bindCallLifecycle(
          nextCall,
          () => {
            setStatus("in-call");
            setCallStartedAt(Date.now());
          },
          resetCallState,
        );
      } catch (error) {
        console.error("Erro ao iniciar ligação:", error);
        resetCallState();
        throw error;
      }
    },
    [ensureDevice, hangUp, resetCallState],
  );

  const acceptIncoming = useCallback(() => {
    const incoming = activeCallRef.current;
    if (!incoming || !incomingCall) return;

    setActiveCallDisplay(incomingCall);
    setIncomingCall(null);
    setStatus("connecting");
    incoming.accept();

    bindCallLifecycle(
      incoming,
      () => {
        setStatus("in-call");
        setCallStartedAt(Date.now());
      },
      resetCallState,
    );
  }, [incomingCall, resetCallState]);

  const rejectIncoming = useCallback(() => {
    activeCallRef.current?.reject();
    resetCallState();
  }, [resetCallState]);

  useEffect(() => {
    if (!voice?.enabled) {
      return;
    }

    ensureDevice().catch((error) => {
      console.error("Erro ao registrar voz Twilio:", error);
    });
  }, [ensureDevice, voice?.enabled]);

  useEffect(() => {
    return () => {
      activeCallRef.current?.disconnect();
      deviceRef.current?.destroy();
      deviceRef.current = null;
    };
  }, []);

  return {
    enabled: Boolean(voice?.enabled),
    status,
    incomingCall,
    activeCallDisplay,
    callStartedAt,
    muted,
    call,
    hangUp,
    toggleMute,
    acceptIncoming,
    rejectIncoming,
  };
};
